"use client";
import { useAnalytics } from "@areahub/adapters";
import colors from "@areahub/theme/src/foundations/colors";
import { Box, Flex, Heading, Text } from "@chakra-ui/react";
import { Button, Button_T } from "@ui/components/button";
import { ImageFragment } from "lib/gcms";
import Image from "next/image";
import { usePathname } from "next/navigation";

export interface IndustriesSpecificHeroProps {
  title: string;
  /**
   * Text above the title
   */
  subtitle: string;
  /**
   * CTA buttons
   */
  buttons: Button_T[];
  /**
   * Description text below the title
   */
  pushText: string;
  image: ImageFragment & { alt?: string };
  mobileImage?: ImageFragment & { alt?: string };
  bg: string;
  titleColor: string;
  subtitleColor: string;
}

export const IndustriesSpecificHero = (
  props: IndustriesSpecificHeroProps & { slug: string },
) => {
  const analytics = useAnalytics();
  const pathname = usePathname();
  const notSectorPages =
    pathname !== "/api-solutions" &&
    pathname !== "/location-reports" &&
    pathname !== "/enterprise-solutions";

  return (
    <Box
      position="relative"
      backgroundImage={`url(${props?.image?.url})`}
      backgroundSize={"cover"}
    >
      <Box
        position="absolute"
        top={0}
        left={0}
        width="100%"
        height="100%"
        backgroundColor="rgba(0, 0, 0, 0.4)"
        zIndex={1}
      />
      <Flex
        flexDir={{
          base: "column",
          lg: "row",
        }}
        pt={{
          base: 16,
          lg: 24,
        }}
        pl={{
          base: 5,
          md: 10,
          xl: 0,
        }}
        pb={{
          base: 12,
          lg: 20,
          xl: 28,
        }}
        pr={{
          base: 5,
          md: 10,
          lg: 0,
        }}
        overflow="visible"
        position="relative"
        maxW="1215px"
        mx="auto"
        zIndex={2}
        gap={10}
      >
        <Flex
          flexDir="column"
          w={{
            lg: "80%",
          }}
          flex={notSectorPages ? "" : "1"}
        >
          {props.mobileImage && (
            <Box
              display={{
                base: "block",
                lg: "none",
              }}
              maxW="350px"
              mx="auto"
              mb={5}
            >
              <Image
                {...props.mobileImage}
                loading="eager"
                priority
                src={props.mobileImage?.url as string}
                alt={props.mobileImage?.alt as string}
                height={props.mobileImage?.height}
                width={props.mobileImage?.width}
                placeholder={
                  typeof props.mobileImage?.blurDataURL === "string"
                    ? "blur"
                    : undefined
                }
                blurDataURL={
                  typeof props.mobileImage?.blurDataURL === "string"
                    ? props.mobileImage?.blurDataURL
                    : undefined
                }
              />
            </Box>
          )}
          <Flex flexDir={"row"} alignItems="center">
            <Heading
              as={"h2"}
              fontSize={{
                base: 18,
                lg: 20,
              }}
              fontWeight="bold"
              color={props.subtitleColor}
              mb={{
                base: 4,
                lg: 6,
              }}
              mr={1}
              position="relative"
              lineHeight={1.3}
            >
              {props.subtitle}
            </Heading>
            {notSectorPages ? (
              <Box
                bgColor={props.subtitleColor}
                flex="1"
                h={{
                  base: "2px",
                  lg: "3px",
                }}
                mb={{
                  base: 4,
                  lg: 6,
                }}
              />
            ) : (
              <></>
            )}
          </Flex>
          <Heading
            as={"h1"}
            fontSize={{
              base: 26,
              md: 34,
              lg: 38,
              xl: 45,
            }}
            textColor={props.titleColor}
            fontWeight={"bold"}
            lineHeight={1.2}
            mb={props.pushText ? 5 : 10}
          >
            {props.title}
          </Heading>
          {props.pushText && (
            <Text
              mb={12}
              fontSize={{
                base: 16,
                md: 18,
                lg: 19,
                xl: 20,
              }}
              fontWeight={"medium"}
              textColor={props.titleColor}
            >
              {props.pushText}
            </Text>
          )}
          <Flex
            flexDir={{
              base: "column",
              lg: "row",
            }}
            alignItems={{
              base: "center",
              lg: "unset",
            }}
            gap={{
              base: 4,
              lg: 5,
            }}
          >
            {props.buttons.map((btn, idx) => (
              <Button
                key={`hero-btn-${idx}`}
                bgColor={
                  notSectorPages
                    ? colors.enterpriseColors.blue["50"]
                    : idx === 0
                      ? colors.enterpriseColors.blue["200"]
                      : colors.enterpriseColors.blue["500"]
                }
                color={
                  notSectorPages ? colors.enterpriseColors.blue["500"] : "white"
                }
                fontSize={{
                  base: 12,
                  md: 14,
                  lg: 16,
                  xl: 18,
                }}
                fontWeight={"bold"}
                py={{
                  base: "6",
                  lg: "8",
                }}
                onClick={() =>
                  idx === 0 &&
                  analytics.track(
                    `User clicked 'Request a Demo' button on ${props.slug} landing page`,
                    {
                      ui: "IndustriesSpecificHero",
                    },
                  )
                }
                textTransform={"uppercase"}
                {...btn}
              />
            ))}
          </Flex>
        </Flex>

        {props.mobileImage && (
          <Box
            display={{
              base: "none",
              lg: "block",
            }}
            maxW="500px"
            flex="1"
          >
            <Image
              {...props.mobileImage}
              loading="eager"
              priority
              src={props.mobileImage?.url as string}
              alt={props.mobileImage?.alt as string}
              height={props.mobileImage?.height}
              width={props.mobileImage?.width}
              placeholder={
                typeof props.mobileImage?.blurDataURL === "string"
                  ? "blur"
                  : undefined
              }
              blurDataURL={
                typeof props.mobileImage?.blurDataURL === "string"
                  ? props.mobileImage?.blurDataURL
                  : undefined
              }
            />
          </Box>
        )}
      </Flex>
    </Box>
  );
};
